import React, { useCallback, useEffect, useState } from "react";
import { Box } from "@mui/material";
import { useSnackbar } from "notistack";
import sh from "bundles/common/utils/sh";
import CvTable from "bundles/Internal/components/cv_vault/CvTable";
import { EMPTY_FILTERS, STAGE_OPTIONS } from "bundles/Internal/components/cv_vault/CvTableFilters";
import { useCandidateModal } from "bundles/Internal/components/common/CandidateModalProvider";

function buildParams(query, pageNum, limit, filters) {
  const statuses = (filters.stage || []).flatMap(
    (key) => STAGE_OPTIONS.find((option) => option.value === key)?.statuses || []
  );

  return {
    query: query || undefined,
    page: pageNum,
    limit,
    everyone: (filters.owner || []).length === 0 ? true : undefined,
    internal_user_id: (filters.owner || []).length > 0 ? filters.owner : undefined,
    status: statuses.length > 0 ? statuses : undefined,
    scanned: (filters.scanned || []).length === 1 ? filters.scanned[0] : undefined,
    region: (filters.region || []).length > 0 ? filters.region : undefined,
  };
}

const CvTablePage = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { openCreateCandidate } = useCandidateModal();

  const [items, setItems] = useState([]);
  const [owners, setOwners] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const fetchItems = useCallback((query, pageNum, limit, activeFilters) => {
    setLoading(true);
    sh.get("/internal/cv_vault_items", { params: buildParams(query, pageNum, limit, activeFilters) })
      .then((res) => {
        setItems(res.data?.items || []);
        setTotalCount(res.data?.pagy?.count || 0);
      })
      .catch(() => enqueueSnackbar("Could not load the CVs", { variant: "error" }))
      .finally(() => setLoading(false));
  }, [enqueueSnackbar]);

  useEffect(() => {
    sh.get("/internal/search/internal_users", { params: { all: 1 } })
      .then((res) => setOwners(Array.isArray(res.data) ? res.data : []))
      .catch(() => setOwners([]));
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
      fetchItems(searchQuery, 1, rowsPerPage, filters);
    }, 250);
    return () => clearTimeout(timer);
  }, [fetchItems, searchQuery, rowsPerPage, filters]);

  const handlePageChange = useCallback((newPage) => {
    setPage(newPage);
    fetchItems(searchQuery, newPage, rowsPerPage, filters);
  }, [fetchItems, searchQuery, rowsPerPage, filters]);

  const handleRowsPerPageChange = useCallback((newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setPage(1);
  }, []);

  const createCandidate = (values, { reset, close, autoEmail } = {}, itemId) =>
    sh.post("/internal/candidates", { candidate: values, auto_email: autoEmail }).then(
      (res) => {
        enqueueSnackbar("Candidate created", { variant: "success" });
        reset?.();
        close?.();
        setItems((prev) =>
          prev.map((row) =>
            row.id === itemId ? { ...row, status: "converted", candidate_id: res.data?.id } : row
          )
        );
      },
      (err) => {
        const messages = err?.response?.data?.errors;
        if (Array.isArray(messages) && messages.length > 0) {
          messages.forEach((msg) => enqueueSnackbar(msg, { variant: "error" }));
          return;
        }
        enqueueSnackbar("Failed to create candidate", { variant: "error" });
      }
    );

  const handleConvert = useCallback((item) => {
    sh.get(`/internal/cv_vault_items/${item.id}`)
      .then((res) => {
        const data = res.data || {};
        openCreateCandidate({
          cvVaultItemId: item.id,
          cvFilename: item.filename,
          parsedPayload: { ...(data.payload || {}), detected_language: data.detected_language },
          cvText: data.cv_text,
          existingResumeUrl: data.file_url,
          onSubmit: (values, helpers) => createCandidate(values, helpers, item.id),
        });
      })
      .catch(() => enqueueSnackbar("Could not open this CV", { variant: "error" }));
  }, [enqueueSnackbar, openCreateCandidate]);

  return (
    <Box
      sx={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        background: "linear-gradient(180deg, #F9FAFF 0%, #EEF0F6 100%)",
      }}
    >
      <CvTable
        items={items}
        filters={filters}
        onFiltersChange={setFilters}
        owners={owners}
        onQueryChange={setSearchQuery}
        onConvert={handleConvert}
        page={page}
        totalCount={totalCount}
        rowsPerPage={rowsPerPage}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        loading={loading}
      />
    </Box>
  );
};

export default CvTablePage;
